// =============================================
// COMPONENTE: TEMPLATES MODAL
// =============================================
// Arquivo: TemplatesModal.tsx
// Descrição: Modal para selecionar templates de fluxo
// =============================================

import React, { useState } from 'react';
import { Node, Edge } from 'reactflow';
import { X, Download, Plus, Search, Sparkles } from 'lucide-react';
import { flowTemplates, FlowTemplate } from './flowTemplates';
import ModalPortal from '../Common/ModalPortal';
import './TemplatesModal.css';

// =============================================
// INTERFACES
// =============================================

interface TemplatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (nodes: Node[], edges: Edge[], mode: 'replace' | 'add') => void;
}

// =============================================
// ÍCONES POR CATEGORIA
// =============================================

const categoryIcons: Record<string, string> = {
  atendimento: 'fa-comments',
  vendas: 'fa-chart-line',
  suporte: 'fa-headset',
  marketing: 'fa-bullhorn',
  frota: 'fa-truck',
  outros: 'fa-shapes'
};

const categoryColors: Record<string, string> = {
  atendimento: '#4299e1',
  vendas: '#48bb78',
  suporte: '#ed8936',
  marketing: '#9f7aea',
  frota: '#0ea5e9',
  outros: '#718096'
};

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const TemplatesModal: React.FC<TemplatesModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(null);
  const [importMode, setImportMode] = useState<'replace' | 'add'>('replace');

  // =============================================
  // FILTROS
  // =============================================

  const categories = Array.from(new Set(flowTemplates.map(t => t.category)));

  const filteredTemplates = flowTemplates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // =============================================
  // HANDLERS
  // =============================================

  const handleSelectTemplate = (template: FlowTemplate) => {
    setSelectedTemplate(template);
  };

  const handleApplyTemplate = () => {
    if (!selectedTemplate) return;
    onSelectTemplate(selectedTemplate.nodes, selectedTemplate.edges, importMode);
    onClose();
    setSelectedTemplate(null);
  };

  const handleClose = () => {
    onClose();
    setSelectedTemplate(null);
    setSearchTerm('');
    setSelectedCategory('all');
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="templates-modal-overlay" onClick={handleClose}>
        <div className="templates-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="templates-modal-header">
          <div className="templates-modal-title">
            <Sparkles size={20} />
            <h2>Templates de Fluxos</h2>
          </div>
          <button className="close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Busca e Filtros */}
        <div className="templates-filters">
          <div className="templates-search">
            <Search size={18} />
            <input
              type="text"
              placeholder="Buscar templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="templates-categories">
            <button
              className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedCategory('all')}
            >
              Todos ({flowTemplates.length})
            </button>
            {categories.map(category => (
              <button
                key={category}
                className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
                style={{
                  borderColor: selectedCategory === category ? categoryColors[category] : undefined,
                  color: selectedCategory === category ? categoryColors[category] : undefined
                }}
              >
                <i className={`fas ${categoryIcons[category]}`}></i>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="templates-content">
          {/* Lista de Templates */}
          <div className="templates-list">
            {filteredTemplates.length === 0 ? (
              <div className="templates-empty">
                <Search size={48} />
                <h3>Nenhum template encontrado</h3>
                <p>Tente ajustar sua busca ou filtros</p>
              </div>
            ) : (
              filteredTemplates.map(template => (
                <div
                  key={template.id}
                  className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div
                    className="template-card-icon"
                    style={{ background: categoryColors[template.category] }}
                  >
                    <i className={`fas ${categoryIcons[template.category]}`}></i>
                  </div>
                  <div className="template-card-content">
                    <h3>{template.name}</h3>
                    <p>{template.description}</p>
                    <div className="template-card-meta">
                      <span className="template-nodes">
                        <i className="fas fa-circle-nodes"></i>
                        {template.nodes.length} nós
                      </span>
                      <div className="template-tags">
                        {template.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="template-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {selectedTemplate?.id === template.id && (
                    <div className="template-card-selected">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Preview e Ações */}
          {selectedTemplate && (
            <div className="template-preview">
              <div className="template-preview-header">
                <h3>Pré-visualização</h3>
              </div>

              <div className="template-preview-info">
                <div className="template-preview-icon" style={{ background: categoryColors[selectedTemplate.category] }}>
                  <i className={`fas ${categoryIcons[selectedTemplate.category]}`}></i>
                </div>
                <div>
                  <h4>{selectedTemplate.name}</h4>
                  <p className="template-preview-category">
                    {selectedTemplate.category.charAt(0).toUpperCase() + selectedTemplate.category.slice(1)}
                  </p>
                </div>
              </div>

              <p className="template-preview-description">
                {selectedTemplate.description}
              </p>

              <div className="template-preview-stats">
                <div className="stat">
                  <i className="fas fa-circle-nodes"></i>
                  <span>{selectedTemplate.nodes.length} nós</span>
                </div>
                <div className="stat">
                  <i className="fas fa-diagram-project"></i>
                  <span>{selectedTemplate.edges.length} conexões</span>
                </div>
              </div>

              <div className="template-preview-tags">
                {selectedTemplate.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>

              {/* Modo de aplicação */}
              <div className="template-apply-mode">
                <label>Como aplicar este template:</label>
                <div className="radio-group">
                  <label className={`radio-option ${importMode === 'replace' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="applyMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                    />
                    <div className="radio-content">
                      <i className="fas fa-sync-alt"></i>
                      <div>
                        <span className="radio-title">Substituir Fluxo</span>
                        <span className="radio-description">Remove o fluxo atual</span>
                      </div>
                    </div>
                  </label>
                  <label className={`radio-option ${importMode === 'add' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="applyMode"
                      value="add"
                      checked={importMode === 'add'}
                      onChange={() => setImportMode('add')}
                    />
                    <div className="radio-content">
                      <i className="fas fa-plus-circle"></i>
                      <div>
                        <span className="radio-title">Adicionar ao Fluxo</span>
                        <span className="radio-description">Mantém nós existentes</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Botões de ação */}
              <div className="template-preview-actions">
                <button className="btn-secondary" onClick={handleClose}>
                  Cancelar
                </button>
                <button className="btn-primary" onClick={handleApplyTemplate}>
                  {importMode === 'replace' ? <Download size={16} /> : <Plus size={16} />}
                  {importMode === 'replace' ? 'Usar Template' : 'Adicionar Template'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
    </ModalPortal>
  );
};

export default TemplatesModal;
